import { FC, useEffect, useState, useRef } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
  LeftAlignedCellBox,
  RightAlignedCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import {
  OrderPayment,
  OrderPaymentGridProps,
} from 'src/models/OrderPayment.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { useGetOrderPayments } from 'src/services/orderPayment.service';
import OrderPaymentActionMenu from 'src/components/ActionButtons/OrderPaymentActionMenu';
import { loadData } from 'src/utils/grid.utils';
import { pagination } from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import {
  getOrderPaymentStatusText,
  getPaymentModeText,
} from 'src/utils/status.utils';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import { SortBy, SortOrder } from 'src/models/Sort.types';

const OrderPaymentGrid: FC<OrderPaymentGridProps> = ({
  orderId,
  state,
  banks,
  onBanksFetch,
  onPaymentModeChange,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    pagination.DEFAULT_PAGE_SIZE
  );
  const { loading, payments, totalItems, fetchPayments, deletePayment } =
    useGetOrderPayments();
  const [sortBy, setSortBy] = useState<SortBy>('paymentDate');
  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'paymentDate', sort: 'desc' },
  ]);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (orderId) {
      loadData(
        () => fetchPayments(orderId, page, pageSize, sortBy, order),
        showSnackbar
      );
    }
  }, [orderId, page, pageSize, sortBy, order, refreshKey]);

  // Reset to page 1 whenever a new payment is created (state toggles)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (page === pagination.DEFAULT_PAGE) {
      setRefreshKey((prev) => prev + 1);
    } else {
      setPage(pagination.DEFAULT_PAGE);
    }
  }, [state]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setPage(pagination.DEFAULT_PAGE);
    setSortBy((sort?.field as SortBy) || 'paymentDate');
    setOrder((sort?.sort as SortOrder) || SortOrder.DESC);
  };

  const columns: GridColDef[] = [
    {
      field: 'rowNumber',
      headerName: '#',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => (
        <LeftAlignedCellBox>{params.value}</LeftAlignedCellBox>
      ),
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 0.5,
      renderCell: (params) => (
        <LeftAlignedCellBox>{params.value}</LeftAlignedCellBox>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.5,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      renderCell: (params) => (
        <RightAlignedCellBox>
          {formatCurrency(params.value)}
        </RightAlignedCellBox>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'paymentMode',
      headerName: 'Payment Mode',
      flex: 0.9,
      renderCell: (params) => (
        <LeftAlignedCellBox>{params.value}</LeftAlignedCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <OrderPaymentActionMenu
          rowId={params.id}
          id={params.row.id}
          orderId={orderId}
          status={params.row.rawStatus}
          paymentMode={params.row.rawPaymentMode}
          banks={banks}
          onBanksFetch={onBanksFetch}
          onPaymentModeChange={onPaymentModeChange}
          fetchPayments={() =>
            fetchPayments(orderId, page, pageSize, sortBy, order)
          }
          deletePayment={async (id: string) => {
            await deletePayment(id);
            if (page === pagination.DEFAULT_PAGE) {
              setRefreshKey((prev) => prev + 1);
            } else {
              setPage(pagination.DEFAULT_PAGE);
            }
          }}
        />
      ),
    },
  ];

  const transformData = (payments: OrderPayment[]) => {
    if (!payments || payments.length === 0) {
      return [];
    }

    return payments.map((payment, index) => {
      let paymentModeDisplay = '';
      const paymentModeValue =
        typeof payment.paymentMode === 'string'
          ? payment.paymentMode
          : getPaymentModeText(payment.paymentMode as PaymentMode);

      // Display format based on payment mode
      if (payment.paymentMode === PaymentMode.CHEQUE) {
        paymentModeDisplay = `Cheque - ${payment.chequeDetails || '-'}`;
      } else if (payment.paymentMode === PaymentMode.ONLINE) {
        paymentModeDisplay = `Online - ${payment.bankAccount || '-'}`;
      } else {
        paymentModeDisplay = paymentModeValue;
      }

      return {
        id: payment.id,
        rowNumber: (page - 1) * pageSize + index + 1,
        paymentDate: new Date(payment.paymentDate).toLocaleDateString('en-GB'),
        amount: payment.paidAmount,
        status: getOrderPaymentStatusText(payment.status),
        rawStatus: payment.status,
        paymentMode: paymentModeDisplay,
        rawPaymentMode: payment.paymentMode,
      };
    });
  };

  const rows = transformData(payments);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default OrderPaymentGrid;
